// Angular dependencies
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { environment } from 'src/environments/environment';

// Modules imported
import { AppRoutingModule } from './app-routing.module';
import { TDDSharedModule } from './shared/tdd/tdd-shared.module';
import { TaxSharedModule } from './shared/tax-suite/tax-shared.module';
import { AcrModule } from './modules/acr/acr.module';
import { ErrorModalComponent } from './shared/tdd/components/error-modal/error-modal.component';

// Third Party dependencies
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { LogLevel } from 'msal';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';

// Components
import { AppComponent } from './app.component';

// Services TDD
import { CommonsService } from './api/services/tdd/common.service';
import { ToastService } from './api/services/tdd/toast.service';
import { ProjectService } from './api/services/tdd/project.service';
import { UserService } from './api/services/tdd/user.service';
import { ManagementService } from './api/services/tdd/management.service';
import { CompanyService } from './api/services/tdd/company.service';
import { TraWhtpService } from './api/services/tdd/tra-whtp.service';
import { ExportExcelService } from './api/services/tdd/export-excel.service';
import { ErrorHandlingInterceptor } from './api/interceptors/error-handling.interceptor';
import { AuthService } from './api/auth/auth.service';

// Services ATRIO
import { AtrioService } from './api/services/atrio/atrio.service';
import { ClientsService } from './api/services/atrio/clients.service';
import { EmployeesService } from './api/services/atrio/employees.service';
import { ServicesService } from './api/services/atrio/services.service';
import { AtrioBreadcrumbService } from './api/services/atrio/breadcrumb.service';
import { TravelPathwaysService } from './api/services/atrio/travelPathways.service';
import { IncomeDetailsService } from './api/services/atrio/income-details.service';
import { NationalIncomeTaxReturnService } from './api/services/atrio/national-income-tax-return.service';
import { EympDeliveryService } from './api/services/atrio/eymp-delivery.service';
import { MsalIdTokenInterceptor } from './api/interceptors/msal-id-token.interceptor';
import { CaseManagementService } from './api/services/atrio/case-management.service';
import { TaxToastService } from './api/services/atrio/toast.service';

import { LeaveRouteConfirmGuard } from './api/guards/leave-route-confirm.guard';
import { ACRClientsService } from './modules/acr/arc-clients/shared/table-view/acr-service.service';
import { ActivityHistoryService } from './api/services/atrio/activity-history.service';
import { SendgridService } from './api/services/atrio/sendgrid.service';
import { WealthDetailsService } from './api/services/atrio/wealth-details.service';
import { SharepointAtrioService } from './api/services/atrio/sharepoint-atrio.service';
import { TokenModalComponent } from './shared/tax-suite/components/token-modal/token-modal.component';
import { FileUploadAntivirusInterceptor } from './api/interceptors/file-antivirus.interceptor';
import { EngCodesSitesService } from './api/services/atrio/eng_codes_sites.service';
import { PostalCodesService } from './api/services/atrio/postal_codes.service';
import { AmlMercurySearcherComponent } from './modules/aml-searcher/aml-mercury-searcher/aml-mercury-searcher.component';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  MOTIF_DATE_PICKER_LOCALE,
  MotifDatePickerModule,
  MotifModule,
  MotifNativeDateTimeModule,
} from '@ey-xd/ng-motif';
import { ManagementNotificationsService } from './api/services/notifications/management.service';
import { PowerAutomateService } from './shared/services/power-automate.service';

import { CorrelationIdInterceptor } from './api/interceptors/correlation-id.interceptor';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

declare global {
  interface Navigator {
    msSaveBlob: (blob: Blob, fileName: string) => boolean;
  }
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: authObject,
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
      iframeHashTimeout: 10000,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  // protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read', 'user.read.all', 'https://graph.microsoft.com/.default']);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
    'user.read.all',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      // scopes: ['https://graph.microsoft.com/.default']
      scopes: ['user.read', 'openid', 'profile'],
    },
    loginFailedRoute: '/login-failed',
  };
}

export function initializeAuthentication(
  authService: AuthService
): () => Promise<void> {
  return () => authService.initializeAuthentication();
}

const authObject = {
  clientId: environment.clientID,
  authority: 'https://login.microsoftonline.com/' + environment.tenantID,
  redirectUri: environment.redirectUrl,
  postLogoutRedirectUri: environment.redirectUrl,
  navigateToLoginRequestUrl: true,
};

@NgModule({
  declarations: [
    AppComponent,
    TokenModalComponent,
    AmlMercurySearcherComponent,
    ErrorModalComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    MotifModule,
    BrowserModule,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    TDDSharedModule,
    TaxSharedModule,
    NgbModule,
    MsalModule,
    AcrModule,
    MotifDatePickerModule,
    MotifNativeDateTimeModule,
    NoopAnimationsModule,
  ],
  providers: [
    HttpClient,

    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },

    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthentication,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },

    MsalService,
    MsalGuard,
    MsalBroadcastService,

    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalIdTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FileUploadAntivirusInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptor,
      multi: true,
    },

    { provide: MOTIF_DATE_PICKER_LOCALE, useValue: 'es-ES' },

    CommonsService,
    ToastService,
    TaxToastService,
    ProjectService,
    UserService,
    ManagementService,
    ManagementNotificationsService,
    CompanyService,
    TraWhtpService,
    ExportExcelService,
    AuthService,
    ActivityHistoryService,
    SendgridService,
    AtrioService,
    ClientsService,
    EmployeesService,
    ServicesService,
    TravelPathwaysService,
    AtrioBreadcrumbService,
    IncomeDetailsService,
    EngCodesSitesService,
    WealthDetailsService,
    NationalIncomeTaxReturnService,
    EympDeliveryService,
    CaseManagementService,
    LeaveRouteConfirmGuard,
    ACRClientsService,
    SharepointAtrioService,
    PowerAutomateService,
    PostalCodesService,

    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
